var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { iif } from "../../logic/iif";
import { isNilOrEmpty } from "../../value/isNilOrEmpty";
/**
 * Generic token provider intended for use inside of Escher/Web-Pay. Tokens are cached in the browser's sessionStorage
 * along with an expiration date. For development purposes a JWT can be provided instead.
 *
 * @category Http
 * @param {string} sessionKeyPrefix - Prefix for naming the two session keys that cache the token.
 * @param {string} gatewayTokenUrl - The gateway token URL to perform an HTTP GET on.
 * @param {number} expirationThresholdMilliseconds - Number of milliseconds ahead of expiration to get a new token.
 * @default {expirationThresholdMilliseconds} - 300,000 ms or Five Minutes (5 * 60 * 1000)
 * @param {boolean} forceWindowReloadOnError - Execute `window.location.reload()` when an HTTP error occurrs while attempting to obtain a token.
 * @default {forceWindowReloadOnError} - false - this can cause an infinite page reload if the URL is wrong. Otherwise useful to kick users out if their session has expired.
 * @returns {Promise<string>} - A JWT token.
 * @example
 * import { webPayTokenProvider } from '@citrus/utils';
 *
 * const gatewayTokenUrl = '/Escher/Escher_WebUI/GatewayToken';
 *
 * // Alternatively use a JWT in lieu of a URL for development purposes only.
 * const token = await webPayTokenProvider('your GET URL here');
 *
 * // Your token is saved to sessionStorage with these keys
 * // {sessionKeyPrefix}-token
 * // {sessionKeyPrefix}-token-expiration-date
 */
var webPayTokenProvider = function (sessionKeyPrefix, gatewayTokenUrl, expirationThresholdMilliseconds, forceWindowReloadOnError) {
    if (expirationThresholdMilliseconds === void 0) { expirationThresholdMilliseconds = 300000; }
    if (forceWindowReloadOnError === void 0) { forceWindowReloadOnError = false; }
    return __awaiter(void 0, void 0, void 0, function () {
        var Empty, keyTokenSuffix, keyTokenExpirationDateSuffix, keyToken, keyTokenExpirationDate, msNow, token, strExpiry, msExpiry, millisecondsRemaining, response, _a, newToken, dtmNow;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    // This is for development purposes only. The argument can be a JWT.
                    if (gatewayTokenUrl.split(".").length === 3) {
                        return [2 /*return*/, gatewayTokenUrl];
                    }
                    Empty = "";
                    keyTokenSuffix = "token";
                    keyTokenExpirationDateSuffix = "token-expiration-date";
                    keyToken = "".concat(sessionKeyPrefix, "-").concat(keyTokenSuffix);
                    keyTokenExpirationDate = "".concat(sessionKeyPrefix, "-").concat(keyTokenExpirationDateSuffix);
                    msNow = Date.now();
                    token = sessionStorage.getItem(keyToken);
                    strExpiry = sessionStorage.getItem(keyTokenExpirationDate);
                    msExpiry = iif(isNilOrEmpty(strExpiry), msNow, new Date(strExpiry).getTime());
                    millisecondsRemaining = msExpiry - msNow;
                    // Use existing token if available
                    if (token && millisecondsRemaining > expirationThresholdMilliseconds) {
                        return [2 /*return*/, token];
                    }
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fetch(gatewayTokenUrl)];
                case 2:
                    response = _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    if (forceWindowReloadOnError) {
                        window.location.reload();
                    }
                    return [2 /*return*/, Empty];
                case 4:
                    if (!response || response.status !== 200) {
                        if (forceWindowReloadOnError) {
                            window.location.reload();
                        }
                        return [2 /*return*/, Empty];
                    }
                    return [4 /*yield*/, response.json()];
                case 5:
                    newToken = _b.sent();
                    dtmNow = new Date();
                    dtmNow.setSeconds(dtmNow.getSeconds() + newToken.expiresIn);
                    sessionStorage.setItem(keyToken, newToken.token);
                    sessionStorage.setItem(keyTokenExpirationDate, dtmNow.toISOString());
                    return [2 /*return*/, newToken.token];
            }
        });
    });
};
export { webPayTokenProvider };
